import React, { useState, useEffect, useCallback } from "react";
import { Users, Loader2, RefreshCw, Award, AlertTriangle, UserX, Sparkles, Play } from "lucide-react";
import { teamGet, teamReview } from "../lib/api";
import { toast } from "sonner";

const GRADE_TONE = { A: "text-emerald-300", B: "text-lime-300", C: "text-[#FFD700]", D: "text-orange-300", F: "text-rose-400" };
const STATUS = {
  good: { label: "Good standing", cls: "bg-emerald-500/10 text-emerald-300" },
  warning: { label: "Final warning", cls: "bg-orange-500/15 text-orange-300" },
  "new-hire": { label: "New hire", cls: "bg-cyan-500/15 text-cyan-200" },
};

const AgentCard = ({ a }) => {
  const st = STATUS[a.status] || STATUS.good;
  const c = a.last_card || {};
  return (
    <div className={`bg-[#0a1020] border rounded-xl p-3 space-y-2 ${a.agent_of_week ? "border-[#FFD700]" : "border-[#ffffff12]"}`} data-testid={`team-agent-${a.seat}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-cyan-50 font-bold text-sm flex items-center gap-1.5">
            {a.name}{a.agent_of_week && <Award className="w-4 h-4 text-[#FFD700]" data-testid={`team-aow-${a.seat}`} />}
            {a.gate_badge && <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono tracking-wider ${a.gate_stage === "LIVE_QUARTER" ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}`}
              data-testid={`team-gate-badge-${a.seat}`}>{a.gate_badge}</span>}
          </p>
          <p className="text-cyan-300/50 text-[11px]">{a.role} · hire #{a.generation || 1}</p>
        </div>
        <div className="text-right shrink-0">
          <p className={`text-2xl font-black leading-none ${GRADE_TONE[a.grade_30d] || "text-cyan-200/40"}`} data-testid={`team-grade-${a.seat}`}>{a.grade_30d || "—"}</p>
          <p className="text-cyan-300/40 text-[10px]">{a.score_30d != null ? `${a.score_30d}/100 · 30d` : "not enough data"}</p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 text-[10px]">
        <span className={`px-1.5 py-0.5 rounded ${st.cls}`} data-testid={`team-status-${a.seat}`}>{st.label}</span>
        {a.warnings > 0 && <span className="px-1.5 py-0.5 rounded bg-orange-500/10 text-orange-300 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{a.warnings} warning</span>}
        {c.decisions != null && <span className="text-cyan-200/50">last review: {c.good_pass || 0} good · {c.bad_pass || 0} bad · {c.missed || 0} missed</span>}
      </div>
      {a.last_self_review && <p className="text-cyan-100/70 text-xs italic">"{a.last_self_review}"</p>}
      {(a.lessons || []).length > 0 && <p className="text-cyan-300/60 text-[11px]"><span className="text-cyan-300/40">Lesson:</span> {a.lessons[a.lessons.length - 1]}</p>}
    </div>
  );
};

const Missed = ({ items }) => (
  <div className="space-y-1.5" data-testid="team-missed">
    {items.map((m, i) => (
      <div key={i} className="bg-[#0a1020] border border-[#ffffff0d] rounded-lg px-3 py-2 text-xs">
        <p className="text-cyan-50"><span className="font-mono font-bold">{m.ticker}</span> <span className="text-[#FFD700]">{m.move}</span> <span className="text-cyan-300/50">· {m.market} · missed by {m.who}</span></p>
        <p className="text-cyan-200/60">{m.why}</p>
        {m.play && <p className="text-emerald-300/80">Play: {m.play}</p>}
      </div>
    ))}
  </div>
);

export default function NexusTeam({ session }) {
  const userId = session.user_id;
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => { try { setD(await teamGet(userId)); } catch (e) {} }, [userId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (!d?.running) return; const t = setInterval(load, 8000); return () => clearInterval(t); }, [d?.running, load]);

  const runNow = async () => {
    setBusy(true);
    try { await teamReview(userId); toast.success("Review session started — takes a minute or two"); setTimeout(load, 1500); }
    catch (e) { toast.error("Couldn't start the review"); } finally { setBusy(false); }
  };

  if (!d) return <div className="h-full flex items-center justify-center bg-[#030712]"><Loader2 className="w-6 h-6 animate-spin text-cyan-300/40" /></div>;
  const rv = d.latest;
  return (
    <div className="h-full overflow-y-auto bg-[#030712] p-4 sm:p-6" data-testid="team-panel">
      <div className="max-w-3xl mx-auto space-y-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-[#FFD700] font-bold text-2xl flex items-center gap-2"><Users className="w-6 h-6" /> The Team</h2>
            <p className="text-cyan-200/60 text-sm mt-1">Graded A–F against what the market really did. Review sessions run at 8am, 1pm and 8pm (Chicago time). Two D grades in a row means a warning. An F, or another D while on warning, means the agent is fired and replaced.</p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={runNow} disabled={busy || d.running} data-testid="team-run-review"
              className="flex items-center gap-1.5 bg-[#FFD700] text-black text-xs font-bold rounded-lg px-3 py-1.5 disabled:opacity-50">
              {d.running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}{d.running ? "Reviewing…" : "Review now"}
            </button>
            <button onClick={load} data-testid="team-refresh" className="text-cyan-300/50 hover:text-cyan-100 p-2"><RefreshCw className="w-4 h-4" /></button>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-2">{d.roster.map((a) => <AgentCard key={a.seat} a={a} />)}</div>

        {rv && (
          <div className="space-y-3" data-testid="team-latest-review">
            <p className="text-cyan-300/60 text-[11px] uppercase tracking-wider font-bold flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" />Last review · {String(rv.created_at).slice(0, 16).replace("T", " ")} UTC</p>
            {rv.summary && <p className="text-cyan-100/80 text-sm" data-testid="team-review-summary">{rv.summary}</p>}
            {(rv.events || []).map((e, i) => (
              <p key={i} className={`text-xs ${e.type === "fired" ? "text-rose-300" : "text-orange-300"}`} data-testid="team-event">
                {e.type === "fired" ? `Fired ${e.name} (grade ${e.grade}). Hired ${e.replacement}.` : `Warning to ${e.name}: second D grade in a row.`}
              </p>
            ))}
            {(rv.missed || []).length > 0 && <><p className="text-cyan-300/60 text-[11px] uppercase tracking-wider font-bold">Why did we miss it</p><Missed items={rv.missed} /></>}
            {(rv.team_lessons || []).length > 0 && (
              <ul className="text-cyan-200/70 text-xs list-disc pl-4 space-y-0.5" data-testid="team-lessons">{rv.team_lessons.map((l, i) => <li key={i}>{l}</li>)}</ul>
            )}
          </div>
        )}

        {(d.alumni || []).length > 0 && (
          <div className="space-y-1.5" data-testid="team-alumni">
            <p className="text-cyan-300/60 text-[11px] uppercase tracking-wider font-bold flex items-center gap-1.5"><UserX className="w-3.5 h-3.5" />Fired</p>
            {d.alumni.map((a) => <p key={a.id} className="text-cyan-200/50 text-xs">{a.name} · {a.role} · final grade {a.final_grade} · {String(a.fired_at).slice(0, 10)}</p>)}
          </div>
        )}
      </div>
    </div>
  );
}
